"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileCheck, Headphones, Wrench, CircleCheck as CheckCircle2, Clock, ArrowRight, ShieldAlert, PhoneCall, CalendarCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const workflowSteps = [{
  stepNumber: "01",
  title: "Ticket Submission & Triage",
  timeframe: "< 1 Hour Response",
  description: "Your request is logged, assigned a tracking reference, and evaluated by a qualified technical engineer.",
  icon: Headphones,
  details: "Priority categorization based on GMP and operational impact."
}, {
  stepNumber: "02",
  title: "Remote Diagnostics & Review",
  timeframe: "1 - 4 Hours",
  description: "Direct connection with instrumentation specialists to evaluate logs, analytical parameters, and error states.",
  icon: FileCheck,
  details: "Over 65% of operational queries are resolved in this stage."
}, {
  stepNumber: "03",
  title: "On-Site Engineer Dispatch",
  timeframe: "Within 24 Hours",
  description: "Certified field service engineers arrive on-site across Ireland and the UK with pre-staged OEM parts.",
  icon: Wrench,
  details: "Immediate replacement of critical worn or faulted modules."
}, {
  stepNumber: "04",
  title: "Verification & Lifecycle Sign-off",
  timeframe: "Post-Intervention",
  description: "Comprehensive qualification check, calibration validation, and audit-ready service documentation.",
  icon: CheckCircle2,
  details: "Full traceability records provided for quality compliance."
}];
const priorityLevels = [{
  level: "Critical (P1)",
  response: "30 Mins",
  resolution: "< 8 Hours",
  description: "Complete batch stoppage or critical instrument failure in active GMP production line."
}, {
  level: "Standard (P2)",
  response: "2 Hours",
  resolution: "< 24 Hours",
  description: "Degraded performance, calibration anomalies, or secondary system alert with workaround."
}, {
  level: "Scheduled (P3)",
  response: "Same Day",
  resolution: "As Scheduled",
  description: "Routine preventative maintenance, planned qualification (IQ/OQ/PQ), and software updates."
}];
export default function Workflow() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="supportworkflowtimeline" data-nav="light" className="bg-background text-foreground py-16 sm:py-24">
      <div className="page-container space-y-16">
        {/* Section Header */}
        <div className="max-w-3xl space-y-4">
          <Badge variant="secondary" className="gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            <Clock className="size-3.5 text-primary" />
            Support Lifecycle
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground font-default">
            Support Request Workflow &amp; Resolution Timeline
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Standardized response protocols ensuring rapid troubleshooting, on-site intervention, and audit-compliant documentation for regulated laboratories across Ireland and the UK.
          </p>
        </div>

        {/* 4-Step Process Flow */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {workflowSteps.map((step, i) => {
          const Icon = step.icon;
          return <motion.div key={step.stepNumber} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="space-y-3 pb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold tracking-widest text-primary font-mono">
                        {step.stepNumber}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground">
                        <Clock className="size-3 text-primary" />
                        {step.timeframe}
                      </span>
                    </div>
                    <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-primary">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      {step.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-0">
                    <CardDescription className="text-sm leading-normal text-muted-foreground">
                      {step.description}
                    </CardDescription>
                    <Separator />
                    <p className="text-xs text-muted-foreground/90 font-medium">
                      {step.details}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* SLA and Priority Matrix */}
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-foreground font-default">
              Target Resolution Milestones by Severity
            </h3>
            <p className="text-sm text-muted-foreground">
              Clear commitments defined under service agreement contracts to minimize analytical instrument downtime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {priorityLevels.map((priority, i) => <div key={priority.level} data-index={i} className="rounded-xl border border-border bg-card p-6 shadow-xs space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground text-base">
                    {priority.level}
                  </span>
                  <Badge variant={i === 0 ? "default" : "secondary"}>
                    {priority.response} First Contact
                  </Badge>
                </div>

                <div className="rounded-lg bg-secondary/60 p-3 flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Target Resolution</span>
                  <span className="text-sm font-bold text-foreground">{priority.resolution}</span>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  {priority.description}
                </p>
              </div>)}
          </div>
        </div>

        {/* Action / Contact Callout */}
        <div className="rounded-2xl border border-border bg-secondary/40 p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1 max-w-2xl">
            <div className="flex items-center gap-2 text-foreground font-semibold text-lg">
              <ShieldAlert className="size-5 text-primary" />
              Need Immediate Equipment Support or Service Dispatch?
            </div>
            <p className="text-sm text-muted-foreground">
              Our technical engineers are available for phone triage, emergency replacement parts, and planned calibration.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button variant="default" asChild>
              <Link to="/ContactPage">
                Submit Support Request
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/ServicesPage">
                View Maintenance Plans
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}